/**
 * The scoring pipeline, ported from scikit-learn.
 *
 * Three objects produce the score in Python: a `TfidfVectorizer`, a `LinearSVC`, and the
 * `Calibrator` of `src/tune_classifier.py`. All three are arithmetic over a linear model, and this
 * file does that arithmetic.
 *
 * ## The settings that the port depends on
 *
 * The vectoriser was built by `build_vectoriser` with `char` analysis, `ngram_range=(5, 5)`,
 * `lowercase=False`, `sublinear_tf=False`, `binary=False`, `use_idf=True`, and `norm='l2'`.
 *
 * Two of those matter here and are easy to get wrong:
 *
 * 1. `sublinear_tf=False` means the term frequency is the raw count, and not `1 + log(count)`.
 * 2. `lowercase=False` means the case is a feature. Never fold the case of the input.
 *
 * `min_df=2` shaped the vocabulary at fit time. It does nothing at score time.
 *
 * ## The order is not free
 *
 * scikit-learn multiplies by the inverse document frequency and then normalises. Normalising first
 * would give a different vector, because the two operations do not commute.
 *
 * ## Why the whole vocabulary ships
 *
 * The l2 divisor sums over every feature that the document matched. Dropping a low-weight feature
 * changes that divisor, so it changes every score rather than one. The vocabulary cannot be pruned
 * without refitting the model.
 */

import { applyVariant } from './normalise';
import type { Detector, Reading } from './types';

/**
 * How many code points one feature holds, when a caller names no length.
 *
 * **The length is no longer fixed.** Item 27 and item 28 chose 5, and item 35 built a family from
 * 1 to 5. The owner chose trigrams for `claude-opus-5` on 2026-08-17, so every function here takes
 * the length and `Detector.meta.ngram` supplies it.
 */
export const NGRAM = 5;

/**
 * Return the index of one 5-gram in the vocabulary, or -1.
 *
 * The vocabulary is one string of sorted 5-grams with no separator, so a binary search reads it
 * without building a map of 247,286 entries. The comparison reads code units directly, because
 * the exporter proved that no key holds an astral code point. Code unit order then equals the code
 * point order that Python sorted by.
 */
export function lookup(vocab: string, gram: string, ngram: number = NGRAM): number {
  let low = 0;
  let high = vocab.length / ngram - 1;

  while (low <= high) {
    const mid = (low + high) >> 1;
    const at = mid * ngram;

    let sign = 0;
    for (let i = 0; i < ngram; i += 1) {
      const difference = gram.charCodeAt(i) - vocab.charCodeAt(at + i);
      if (difference !== 0) {
        sign = difference;
        break;
      }
    }

    if (sign === 0) return mid;
    if (sign < 0) high = mid - 1;
    else low = mid + 1;
  }
  return -1;
}

/**
 * Return how often each 5-gram appears in the prepared text.
 *
 * The window slides over code points rather than UTF-16 units, so an astral letter does not shift
 * the cut. `letters` must already be the output of `lettersOnly`.
 */
export function countGrams(letters: string, ngram: number = NGRAM): Map<string, number> {
  const points = Array.from(letters);
  const counts = new Map<string, number>();

  for (let i = 0; i + ngram <= points.length; i += 1) {
    const gram = points.slice(i, i + ngram).join('');
    counts.set(gram, (counts.get(gram) ?? 0) + 1);
  }
  return counts;
}

/**
 * Return the classifier margin for one prepared text, and the two match counts.
 *
 * The margin is `coef · x + intercept`, where `x` is the l2-normalised tf-idf vector. A text that
 * matched nothing gives the intercept alone, which is what scikit-learn returns for a zero row.
 */
export function margin(detector: Detector, letters: string): {
  raw: number;
  matched: number;
  total: number;
} {
  const { vocab, idfLevels, idfIndex, coef, meta } = detector;
  const counts = countGrams(letters, meta.ngram);

  let dot = 0;
  let squares = 0;
  let matched = 0;
  let total = 0;

  for (const [gram, count] of counts) {
    total += count;
    const index = lookup(vocab, gram, meta.ngram);
    if (index < 0) continue;

    matched += count;
    const weighted = count * idfLevels[idfIndex[index]];
    dot += coef[index] * weighted;
    squares += weighted * weighted;
  }

  const norm = Math.sqrt(squares);
  const raw = (norm > 0 ? dot / norm : 0) + meta.intercept;
  return { raw, matched, total };
}

/**
 * Return the calibrated probability for one classifier margin.
 *
 * `Calibrator.probability` standardises the margin, reads it through a logistic regression of one
 * feature, and clips the result. The standardising step is not cosmetic: `fit_calibrator` needs it
 * so that the fit is well conditioned, and the saved centre and scale belong to the map.
 */
export function calibrate(detector: Detector, raw: number): number {
  const { centre, scale, coef, intercept } = detector.meta.calibrator;
  const z = (raw - centre) / scale;
  const probability = 1 / (1 + Math.exp(-(coef * z + intercept)));
  return Math.min(1, Math.max(0, probability));
}

/** Return the score of one text, and the numbers behind it. */
export function scoreText(detector: Detector, text: string): Reading {
  const letters = applyVariant(text, detector.meta.variant);
  const { raw, matched, total } = margin(detector, letters);

  return {
    score: calibrate(detector, raw),
    letters: Array.from(letters).length,
    raw,
    matched,
    total,
  };
}
